import { useEffect, useState } from "react";
import {
    FaPlus,
    FaEdit,
    FaTrash,
    FaSearch,
    FaSpinner,
    FaArrowLeft
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { getMenuPermissions } from "../utils/auth";

import "./EmergencyContacts.css";

// Requires: npm install react-toastify

const API_URL = process.env.REACT_APP_API_URL;


function EmergencyContacts() {

    // Mirrors the same menu_key the backend gates this page's
    // endpoints with (require_menu_permission("emergency_contacts")) -
    // hides a button when the click behind it would 403 anyway.
    // Server-side enforcement is unchanged; this is purely display.
    const perms = getMenuPermissions("emergency_contacts");

    const emptyForm = {
        id: "",
        name: "",
        category: "",
        phone: "",
        location: "",
        priority: ""
    };

    const [contacts, setContacts] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [search, setSearch] = useState("");

    // Replaces showModal -- when true, the form is shown INSTEAD of
    // the table (no popup/overlay at all).
    const [showForm, setShowForm] = useState(false);
    const [isEdit, setIsEdit] = useState(false);

    const [form, setForm] = useState(emptyForm);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        loadContacts();
    }, []);

    useEffect(() => {

        const value = search.toLowerCase();

        setFiltered(

            contacts.filter(item =>
                item.name.toLowerCase().includes(value) ||
                item.category.toLowerCase().includes(value) ||
                item.phone.toLowerCase().includes(value) ||
                item.location.toLowerCase().includes(value)
            )

        );

    }, [search, contacts]);

    const loadContacts = async () => {

        setIsLoading(true);

        try {

            const res = await fetch(`${API_URL}/portal/emergency-contacts`);

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            if (Array.isArray(data)) {

                setContacts(data);

            } else if (data.status) {

                setContacts(data.data || []);

            } else {

                setContacts([]);

                toast.error(data.message || "Failed to load contacts");

            }

        } catch (err) {

            console.error(err);

            setContacts([]);

            toast.error("Unable to load contacts. Please try again.");

        } finally {

            setIsLoading(false);

        }

    };

    const handleChange = (e) => {

        setForm({
            ...form,
            [e.target.name]: e.target.value
        });

    };

    const openAdd = () => {

        setIsEdit(false);

        setForm(emptyForm);

        setShowForm(true);

    };

    const openEdit = (item) => {

        setIsEdit(true);

        setForm(item);

        setShowForm(true);

    };

    const closeForm = () => {

        if (isSaving) return; // don't let them leave mid-save

        setShowForm(false);

        setForm(emptyForm);

    };

    const saveContact = async () => {

        if (
            !form.name ||
            !form.category ||
            !form.phone ||
            !form.location
        ) {

            toast.warn("Please fill all fields");
            return;

        }

        setIsSaving(true);

        try {

            let res;

            if (isEdit) {

                res = await fetch(`${API_URL}/portal/emergency-contacts`, {

                    method: "PUT",

                    headers: {
                        "Content-Type": "application/json"
                    },

                    body: JSON.stringify(form)

                });

            } else {

                res = await fetch(`${API_URL}/portal/emergency-contacts`, {

                    method: "POST",

                    headers: {
                        "Content-Type": "application/json"
                    },

                    body: JSON.stringify(form)

                });

            }

            const data = await res.json();

            if (res.ok && data.status) {

                setShowForm(false);

                setForm(emptyForm);

                toast.success(
                    isEdit
                        ? "Contact updated successfully"
                        : "Contact added successfully"
                );

                loadContacts();

            } else {

                toast.error(data.message || "Failed to save contact");

            }

        } catch (err) {

            console.error("Failed to save contact:", err);

            toast.error("Something went wrong while saving the contact");

        } finally {

            setIsSaving(false);

        }

    }
    const deleteContact = async (id) => {

        const ok = window.confirm(
            "Are you sure you want to delete this contact?"
        );

        if (!ok) return;

        setDeletingId(id);

        try {

            const res = await fetch(`${API_URL}/portal/emergency-contacts/${id}`, {

                method: "DELETE"

            });

            const data = await res.json();

            if (res.ok && data.status) {

                toast.success("Contact deleted successfully");

                loadContacts();

            } else {

                toast.error(data.message || "Failed to delete contact");

            }

        } catch (err) {

            console.error("Failed to delete contact:", err);

            toast.error("Something went wrong while deleting the contact");

        } finally {

            setDeletingId(null);

        }

    }

    return (

        <div className="page">

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
            />

            <div className="page-header">

                <h2>Emergency Contacts</h2>

                {

                    !showForm && perms.add &&

                    <button
                        type="button"
                        className="add-btn"
                        onClick={openAdd}
                    >

                        <FaPlus />

                        Add Contact

                    </button>

                }

            </div>

            {

                showForm ? (

                    // ===========================================
                    // INLINE FORM -- shown instead of the table.
                    // No modal/overlay/position:fixed involved.
                    // ===========================================
                    <div className="inline-form-container">

                        <div className="inline-form-header">

                            <button
                                type="button"
                                className="back-btn"
                                onClick={closeForm}
                                disabled={isSaving}
                            >

                                <FaArrowLeft />
                                Back to list

                            </button>

                            <h3>
                                {isEdit ? "Update Contact" : "Add Contact"}
                            </h3>

                        </div>

                        <div className="inline-form-grid">

                            <input
                                name="name"
                                placeholder="Contact Name"
                                value={form.name}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="category"
                                placeholder="Category"
                                value={form.category}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="phone"
                                placeholder="Phone"
                                value={form.phone}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="location"
                                placeholder="Location"
                                value={form.location}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="priority"
                                type="number"
                                placeholder="Priority"
                                value={form.priority}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                        </div>

                        <div className="inline-form-actions">

                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={closeForm}
                                disabled={isSaving}
                            >
                                Cancel
                            </button>

                            <button

                                type="button"

                                className="save-btn"

                                onClick={saveContact}

                                disabled={isSaving}

                            >

                                {

                                    isSaving

                                        ? <><FaSpinner className="spin" /> Saving...</>

                                        : isEdit
                                            ? "Update Contact"
                                            : "Save Contact"

                                }

                            </button>

                        </div>

                    </div>

                ) : (

                    // ===========================================
                    // TABLE VIEW
                    // ===========================================
                    <>

                        <div className="search-box">

                            <FaSearch />

                            <input

                                placeholder="Search by name, phone, category..."

                                value={search}

                                onChange={(e) => setSearch(e.target.value)}

                            />

                        </div>

                        <div className="table-container">

                            <table>

                                <thead>

                                    <tr>

                                        <th>Name</th>
                                        <th>Priority</th>
                                        <th>Category</th>
                                        <th>Phone</th>
                                        <th>Location</th>
                                        <th style={{ width: "120px" }}>
                                            Action
                                        </th>

                                    </tr>

                                </thead>

                                <tbody>

                                    {

                                        isLoading ?

                                            <tr>

                                                <td
                                                    colSpan="6"
                                                    style={{
                                                        textAlign: "center",
                                                        padding: "40px"
                                                    }}
                                                >

                                                    <FaSpinner className="spin" /> Loading contacts...

                                                </td>

                                            </tr>

                                            :

                                            filtered.length === 0 ?

                                                <tr>

                                                    <td
                                                        colSpan="6"
                                                        style={{
                                                            textAlign: "center",
                                                            padding: "40px"
                                                        }}
                                                    >

                                                        No Records Found

                                                    </td>

                                                </tr>

                                                :

                                                filtered.map(item => (

                                                    <tr key={item.id}>

                                                        <td>{item.name}</td>

                                                        <td>{item.priority}</td>

                                                        <td>{item.category}</td>

                                                        <td>{item.phone}</td>

                                                        <td>{item.location}</td>

                                                        <td>

                                                            <div className="action-buttons">

                                                                {perms.edit &&

                                                                <button

                                                                    type="button"

                                                                    className="icon-btn edit-btn"

                                                                    title="Edit contact"

                                                                    aria-label="Edit contact"

                                                                    onClick={() => openEdit(item)}

                                                                    disabled={deletingId === item.id}

                                                                >

                                                                    <FaEdit />

                                                                </button>

                                                                }

                                                                {perms.delete &&

                                                                <button

                                                                    type="button"

                                                                    className="icon-btn delete-btn"

                                                                    title="Delete contact"

                                                                    aria-label="Delete contact"

                                                                    onClick={() => deleteContact(item.id)}

                                                                    disabled={deletingId === item.id}

                                                                >

                                                                    {

                                                                        deletingId === item.id

                                                                            ? <FaSpinner className="spin" />

                                                                            : <FaTrash />

                                                                    }

                                                                </button>
                                                                }

                                                            </div>

                                                        </td>

                                                    </tr>

                                                ))

                                    }

                                </tbody>

                            </table>

                        </div>

                    </>

                )

            }

        </div>

    );

}

export default EmergencyContacts;